import React, { useMemo, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { toast, Toaster } from "sonner";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowLeft, ArrowRight, Check, ChevronRight, ShieldCheck, Sparkles, Zap } from "lucide-react";
import DebtNavbar from "../components/layout/DebtNavbar";
import Footer from "../components/layout/Footer";
import Seo from "../components/Seo";
import api, { formatApiError } from "../lib/api";

/**
 * DebtApplyWizard — Typeform-style step-by-step loan application.
 *
 * Route: `/money/apply/:product`
 *
 * Behaviour:
 *   1. Amount & purpose      (product-specific)
 *   2. Contact + city        ← saves partial lead on next
 *   3. Financial snapshot    ← updates lead
 *   4. Review + consent      ← flips `partial=false`
 *
 * Every request stamps `source_page` + `source_section` so admins can
 * attribute each lead to the exact card / step it came from.
 */

const PRODUCTS = {
  home_loan: {
    title: "Home Loan",
    tagline: "Instant approval at lowest interest rates.",
    minAmount: 500000,
    maxAmount: 100000000,
    defaultAmount: 5000000,
    tenureMax: 360,
    audience: "individual",
    purposeOptions: [
      { key: "buy_ready", label: "Buy a ready-to-move home" },
      { key: "buy_underconstruction", label: "Buy an under-construction home" },
      { key: "self_construction", label: "Self construction on my plot" },
      { key: "renovation", label: "Home renovation / extension" },
      { key: "transfer", label: "Balance transfer from another bank" },
    ],
  },
  personal_loan: {
    title: "Personal Loan",
    tagline: "Paperless process at a low rate.",
    minAmount: 50000,
    maxAmount: 4000000,
    defaultAmount: 500000,
    tenureMax: 60,
    audience: "individual",
    purposeOptions: [
      { key: "wedding", label: "Wedding" },
      { key: "vacation", label: "Vacation / Travel" },
      { key: "medical", label: "Medical emergency" },
      { key: "renovation", label: "Home renovation" },
      { key: "consolidation", label: "Debt consolidation" },
      { key: "other", label: "Other" },
    ],
  },
  car_loan: {
    title: "New Car Loan",
    tagline: "Drive away your dream car today.",
    minAmount: 100000,
    maxAmount: 10000000,
    defaultAmount: 800000,
    tenureMax: 84,
    audience: "individual",
    purposeOptions: [
      { key: "new_car", label: "New car" },
      { key: "used_car", label: "Used / pre-owned car" },
      { key: "refinance", label: "Refinance existing car loan" },
    ],
  },
  lap: {
    title: "Loan Against Property",
    tagline: "Unlock the value of your property.",
    minAmount: 1000000,
    maxAmount: 250000000,
    defaultAmount: 10000000,
    tenureMax: 180,
    audience: "individual",
    purposeOptions: [
      { key: "business", label: "Business expansion" },
      { key: "wedding", label: "Wedding / life event" },
      { key: "medical", label: "Medical emergency" },
      { key: "education", label: "Education abroad" },
      { key: "other", label: "Other" },
    ],
  },
};

const EMPLOYMENT_TYPES = [
  { key: "salaried", label: "Salaried" },
  { key: "self_employed", label: "Self-employed / Business" },
  { key: "professional", label: "Self-employed professional (CA / Doctor)" },
  { key: "student", label: "Student" },
  { key: "retired", label: "Retired" },
];

const fmtINR = (n) => {
  if (!n && n !== 0) return "—";
  if (n >= 1e7) return `₹${(n / 1e7).toFixed(n % 1e7 === 0 ? 0 : 2)} Cr`;
  if (n >= 1e5) return `₹${(n / 1e5).toFixed(0)} L`;
  return `₹${(n / 1000).toFixed(0)} K`;
};

export default function DebtApplyWizard() {
  const { product } = useParams();
  const [params] = useSearchParams();
  const cfg = PRODUCTS[product] || PRODUCTS.personal_loan;
  const nav = useNavigate();

  // `source_section` passes the exact card/section that opened the wizard
  // (e.g. "hero-product-card" vs "products-tab"). Falls back to a safe default.
  const sourceHint = params.get("src") || "wizard-direct";

  const [step, setStep] = useState(0);
  const [busy, setBusy] = useState(false);
  const [leadId, setLeadId] = useState(null);
  const [form, setForm] = useState({
    product,
    amount_inr: cfg.defaultAmount,
    tenure_months: Math.min(60, cfg.tenureMax),
    purpose: cfg.purposeOptions[0]?.key || "other",
    contact_name: "",
    contact_email: "",
    contact_phone: "",
    city: "",
    employment: "salaried",
    monthly_income: "",
    pan_last4: "",
    consent: false,
  });

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const steps = [
    { key: "amount", title: "How much do you need?", desc: "We'll shortlist lenders whose sanctioned range fits." },
    { key: "contact", title: "Where can we reach you?", desc: "We save your progress here — no spam, ever." },
    { key: "profile", title: "Your income snapshot", desc: "So we can pre-check eligibility with the lender panel." },
    { key: "review", title: "Review & submit", desc: "One last look before a specialist calls you." },
  ];

  const total = steps.length;
  const progress = ((step + 1) / total) * 100;

  const source_page = `/money/apply/${product}`;
  const source_section = `wizard-step-${step + 1}${sourceHint ? `-from-${sourceHint}` : ""}`;

  /* -----------------------------------------------------
     Persist a partial lead the moment step 2 (contact) is
     completed so admins see drop-offs mid-wizard.
     ----------------------------------------------------- */
  const persistPartial = async () => {
    try {
      const payload = {
        product,
        source_page,
        source_section,
        contact_name: form.contact_name,
        contact_email: form.contact_email,
        contact_phone: form.contact_phone,
        amount_inr: Number(form.amount_inr) || null,
        tenure_months: Number(form.tenure_months) || null,
        city: form.city,
        employment: form.employment,
        monthly_income: form.monthly_income ? Number(form.monthly_income) : null,
        wizard_step: step + 1,
        partial: true,
        extra: {
          purpose: form.purpose,
          audience: cfg.audience,
          pan_last4: form.pan_last4,
        },
      };
      if (!leadId) {
        const r = await api.post("/public/debt-leads", payload);
        setLeadId(r.data.lead_id);
      } else {
        await api.patch(`/public/debt-leads/${leadId}`, payload);
      }
    } catch (_e) {
      // Silent — drop-off tracking is best-effort. Never block UX.
    }
  };

  const next = async () => {
    if (step === 0 && (!form.amount_inr || form.amount_inr < cfg.minAmount)) {
      toast.error(`Minimum amount is ${fmtINR(cfg.minAmount)}`);
      return;
    }
    if (step === 1) {
      if (!form.contact_name || !form.contact_phone || !form.contact_email) {
        toast.error("Name, phone and email are required.");
        return;
      }
      setBusy(true);
      await persistPartial();
      setBusy(false);
    }
    if (step === 2) {
      setBusy(true);
      await persistPartial();
      setBusy(false);
    }
    setStep((s) => Math.min(total - 1, s + 1));
  };

  const back = () => setStep((s) => Math.max(0, s - 1));

  const submit = async () => {
    if (!form.consent) {
      toast.error("Please accept the consent to continue.");
      return;
    }
    setBusy(true);
    try {
      const payload = {
        product,
        source_page,
        source_section: `wizard-submit${sourceHint ? `-from-${sourceHint}` : ""}`,
        contact_name: form.contact_name,
        contact_email: form.contact_email,
        contact_phone: form.contact_phone,
        amount_inr: Number(form.amount_inr) || null,
        tenure_months: Number(form.tenure_months) || null,
        city: form.city,
        employment: form.employment,
        monthly_income: form.monthly_income ? Number(form.monthly_income) : null,
        wizard_step: total,
        partial: false,
        extra: {
          purpose: form.purpose,
          audience: cfg.audience,
          pan_last4: form.pan_last4,
          product_title: cfg.title,
        },
      };
      if (leadId) {
        await api.patch(`/public/debt-leads/${leadId}`, payload);
      } else {
        const r = await api.post("/public/debt-leads", payload);
        setLeadId(r.data.lead_id);
      }
      toast.success("Application received. A specialist will call you within 1 business day.");
      setStep(total); // show "done" screen
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen text-ink bg-cream" data-testid="debt-apply-wizard">
      <Seo title={`Apply · ${cfg.title} · VenturaZ MONEY`} description={cfg.tagline} path={source_page} />
      <DebtNavbar />

      <section
        className="relative overflow-hidden"
        style={{ background: "linear-gradient(135deg, #1F1408 0%, #3E2410 45%, #7A4E1D 100%)" }}
      >
        <div className="pointer-events-none absolute -top-24 -right-24 w-[420px] h-[420px] rounded-full opacity-30" style={{ background: "radial-gradient(circle, #E9C46A 0%, transparent 60%)" }} />
        <div className="relative max-w-3xl mx-auto px-6 py-10">
          <Link to={`/venturaz-money/product/${product.replace(/_/g, "-")}`} className="inline-flex items-center gap-1 text-xs text-[#E9C46A] hover:underline" data-testid="wizard-back-to-product">
            <ArrowLeft size={12} /> Back to product
          </Link>
          <div className="mt-3 inline-flex items-center gap-1.5 bg-gold text-[#1F1408] text-[10px] font-mono uppercase tracking-[0.22em] font-bold px-2.5 py-1 rounded-full">
            <Sparkles size={11} /> {cfg.title} · Application
          </div>
          <div className="mt-3 font-display text-3xl md:text-4xl leading-tight text-[#FFF3D9]" data-testid="wizard-hero-title">
            {cfg.tagline}
          </div>
          <div className="mt-6">
            <div className="h-1.5 rounded-full bg-white/15 overflow-hidden">
              <div
                className="h-full bg-gold transition-all duration-500"
                style={{ width: `${Math.min(100, progress)}%` }}
                data-testid="wizard-progress-bar"
              />
            </div>
            <div className="mt-2 flex items-center justify-between text-[10px] font-mono uppercase tracking-widest text-[#FFF3D9]/70">
              <span>Step {Math.min(step + 1, total)} of {total}</span>
              <span>Zero platform fee · Free CIBIL</span>
            </div>
          </div>
        </div>
      </section>

      <main className="max-w-3xl mx-auto px-6 py-10">
        <AnimatePresence mode="wait">
          {step === total ? (
            <DoneCard cfg={cfg} leadId={leadId} onGoHome={() => nav("/money")} />
          ) : (
            <motion.div
              key={step}
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -8 }}
              transition={{ duration: 0.25 }}
              className="rounded-2xl border border-[#B8860B]/25 bg-white p-6 md:p-8 shadow-[0_20px_60px_rgba(31,20,8,0.08)]"
              data-testid={`wizard-step-${step + 1}`}
            >
              <div className="text-[10px] font-mono uppercase tracking-widest text-[#7A4E1D] mb-2">
                Step {step + 1} · {steps[step].key.toUpperCase()}
              </div>
              <h2 className="font-display text-2xl md:text-3xl text-ink leading-tight">{steps[step].title}</h2>
              <p className="mt-2 text-sm text-ink-soft">{steps[step].desc}</p>

              <div className="mt-6 space-y-4">
                {step === 0 && <StepAmount cfg={cfg} form={form} set={set} />}
                {step === 1 && <StepContact form={form} set={set} />}
                {step === 2 && <StepProfile form={form} set={set} />}
                {step === 3 && <StepReview cfg={cfg} form={form} set={set} />}
              </div>

              <div className="mt-8 flex items-center justify-between">
                <button
                  onClick={back}
                  disabled={step === 0 || busy}
                  className="text-sm font-heading text-ink-muted disabled:opacity-30 hover:text-ink inline-flex items-center gap-1"
                  data-testid="wizard-back"
                >
                  <ArrowLeft size={14} /> Back
                </button>
                {step < total - 1 ? (
                  <button
                    onClick={next}
                    disabled={busy}
                    className="inline-flex items-center gap-2 px-5 py-2.5 rounded-md bg-[#7A4E1D] text-[#FFF3D9] text-sm font-heading font-bold hover:bg-[#5c3a15] disabled:opacity-60 transition-colors"
                    data-testid="wizard-next"
                  >
                    {busy ? "Saving…" : "Continue"} <ArrowRight size={14} />
                  </button>
                ) : (
                  <button
                    onClick={submit}
                    disabled={busy || !form.consent}
                    className="inline-flex items-center gap-2 px-5 py-2.5 rounded-md bg-gold text-[#1F1408] text-sm font-heading font-bold hover:bg-yellow-400 disabled:opacity-60 transition-colors shadow-[0_10px_30px_rgba(233,196,106,0.35)]"
                    data-testid="wizard-submit"
                  >
                    {busy ? "Submitting…" : "Submit application"} <Check size={14} />
                  </button>
                )}
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        <div className="mt-5 text-[11px] text-ink-muted flex items-start gap-2">
          <ShieldCheck size={14} className="text-[#7A4E1D] mt-0.5 shrink-0" />
          Advisor, never lender. DPDP-compliant. We share your details only with the lenders you approve.
        </div>
      </main>

      <Footer />
      <Toaster richColors position="top-right" />
    </div>
  );
}

/* ---- Step components ------------------------------------------------ */
function StepAmount({ cfg, form, set }) {
  return (
    <>
      <div>
        <label className="text-[11px] font-mono uppercase tracking-widest text-ink-muted">Loan amount ({fmtINR(cfg.minAmount)} – {fmtINR(cfg.maxAmount)})</label>
        <div className="mt-1 flex items-center justify-between">
          <input
            type="range"
            min={cfg.minAmount}
            max={cfg.maxAmount}
            step={Math.max(50000, Math.round((cfg.maxAmount - cfg.minAmount) / 200))}
            value={form.amount_inr}
            onChange={(e) => set("amount_inr", Number(e.target.value))}
            className="w-full accent-[#7A4E1D]"
            data-testid="wizard-amount-slider"
          />
          <div className="ml-4 min-w-24 text-right font-display text-xl text-[#7A4E1D]" data-testid="wizard-amount-value">{fmtINR(form.amount_inr)}</div>
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <FieldSelect label="Purpose" value={form.purpose} onChange={(v) => set("purpose", v)} options={cfg.purposeOptions} testId="wizard-purpose" />
        <FieldNumber label="Tenure (months)" value={form.tenure_months} onChange={(v) => set("tenure_months", v)} min={6} max={cfg.tenureMax} testId="wizard-tenure" />
      </div>
    </>
  );
}
function StepContact({ form, set }) {
  return (
    <>
      <Field label="Full name" value={form.contact_name} onChange={(v) => set("contact_name", v)} testId="wizard-name" />
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Field label="Mobile" value={form.contact_phone} onChange={(v) => set("contact_phone", v)} testId="wizard-phone" placeholder="10-digit mobile" />
        <Field label="Email" type="email" value={form.contact_email} onChange={(v) => set("contact_email", v)} testId="wizard-email" />
      </div>
      <Field label="City" value={form.city} onChange={(v) => set("city", v)} testId="wizard-city" />
      <div className="text-[11px] text-ink-muted flex items-center gap-1"><Zap size={12} className="text-[#7A4E1D]" /> Your progress from here on is saved automatically.</div>
    </>
  );
}
function StepProfile({ form, set }) {
  return (
    <>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <FieldSelect label="Employment type" value={form.employment} onChange={(v) => set("employment", v)} options={EMPLOYMENT_TYPES} testId="wizard-employment" />
        <FieldNumber label="Monthly income (₹)" value={form.monthly_income} onChange={(v) => set("monthly_income", v)} min={0} testId="wizard-income" />
      </div>
      <Field label="PAN (last 4 digits, optional)" value={form.pan_last4} onChange={(v) => set("pan_last4", v.toUpperCase().slice(0, 4))} testId="wizard-pan" placeholder="e.g. XYZ1" />
    </>
  );
}
function StepReview({ cfg, form, set }) {
  return (
    <div className="space-y-4">
      <div className="rounded-lg border border-ink/10 bg-[#FBFAF5] p-4 divide-y divide-ink/[0.05] text-sm">
        <Row k="Product" v={cfg.title} />
        <Row k="Amount" v={fmtINR(form.amount_inr)} />
        <Row k="Tenure" v={`${form.tenure_months} months`} />
        <Row k="Purpose" v={cfg.purposeOptions.find((p) => p.key === form.purpose)?.label || form.purpose} />
        <Row k="Name" v={form.contact_name} />
        <Row k="Mobile · Email" v={`${form.contact_phone} · ${form.contact_email}`} />
        <Row k="City · Employment" v={`${form.city || "—"} · ${EMPLOYMENT_TYPES.find((e) => e.key === form.employment)?.label || form.employment}`} />
        <Row k="Monthly income" v={form.monthly_income ? `₹${Number(form.monthly_income).toLocaleString("en-IN")}` : "—"} />
      </div>
      <label className="flex items-start gap-2 text-[12px] text-ink-soft leading-relaxed" data-testid="wizard-consent-wrap">
        <input
          type="checkbox"
          checked={!!form.consent}
          onChange={(e) => set("consent", e.target.checked)}
          className="mt-1 accent-[#7A4E1D]"
          data-testid="wizard-consent"
        />
        <span>
          I authorise VenturaZ MONEY to contact me, share my details with shortlisted lenders,
          and pull my credit bureau report (soft, no score impact) to assess eligibility.
        </span>
      </label>
    </div>
  );
}
function DoneCard({ cfg, leadId, onGoHome }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
      className="rounded-2xl border border-[#B8860B]/25 bg-white p-8 md:p-10 text-center shadow-[0_20px_60px_rgba(31,20,8,0.08)]"
      data-testid="wizard-done"
    >
      <div className="w-14 h-14 mx-auto rounded-full bg-emerald-100 text-emerald-700 flex items-center justify-center">
        <Check size={28} />
      </div>
      <h2 className="mt-4 font-display text-2xl md:text-3xl text-ink">Application received.</h2>
      <p className="mt-2 text-sm text-ink-soft max-w-lg mx-auto">
        Your {cfg.title.toLowerCase()} application is in our advisor queue. A named specialist will call you within
        <b> 1 business day</b>. Reference number below.
      </p>
      {leadId && (
        <div className="mt-4 inline-block px-3 py-1.5 rounded-full bg-[#FBFAF5] border border-[#B8860B]/20 text-[11px] font-mono uppercase tracking-widest text-[#7A4E1D]" data-testid="wizard-done-ref">
          Ref: {leadId}
        </div>
      )}
      <div className="mt-6 flex items-center justify-center gap-3">
        <Link to="/money" className="text-sm text-[#7A4E1D] hover:underline">← Back to VenturaZ Money</Link>
        <span className="text-ink-muted">·</span>
        <button onClick={onGoHome} className="text-sm text-ink font-heading hover:underline">Explore other products</button>
      </div>
    </motion.div>
  );
}

/* ---- Tiny inputs ---------------------------------------------------- */
function Field({ label, value, onChange, testId, ...rest }) {
  return (
    <div>
      <label className="text-[11px] font-mono uppercase tracking-widest text-ink-muted">{label}</label>
      <input value={value || ""} onChange={(e) => onChange(e.target.value)} data-testid={testId} {...rest}
        className="w-full mt-1 px-3 py-2 border border-ink/15 rounded text-sm text-ink bg-white focus:outline-none focus:border-[#7A4E1D]" />
    </div>
  );
}
function FieldNumber({ label, value, onChange, testId, ...rest }) {
  return (
    <div>
      <label className="text-[11px] font-mono uppercase tracking-widest text-ink-muted">{label}</label>
      <input type="number" value={value ?? ""} onChange={(e) => onChange(e.target.value)} data-testid={testId} {...rest}
        className="w-full mt-1 px-3 py-2 border border-ink/15 rounded text-sm text-ink bg-white focus:outline-none focus:border-[#7A4E1D]" />
    </div>
  );
}
function FieldSelect({ label, value, onChange, options, testId }) {
  return (
    <div>
      <label className="text-[11px] font-mono uppercase tracking-widest text-ink-muted">{label}</label>
      <select value={value || ""} onChange={(e) => onChange(e.target.value)} data-testid={testId}
        className="w-full mt-1 px-3 py-2 border border-ink/15 rounded text-sm text-ink bg-white focus:outline-none focus:border-[#7A4E1D]">
        {options.map((o) => <option key={o.key} value={o.key}>{o.label}</option>)}
      </select>
    </div>
  );
}
function Row({ k, v }) {
  return (
    <div className="flex items-center justify-between py-2">
      <div className="text-[11px] font-mono uppercase tracking-widest text-ink-muted">{k}</div>
      <div className="text-sm font-heading text-ink text-right">{v}</div>
    </div>
  );
}
